import { renderDjotToHtml } from '@/lib/djot'
import { tagNameEquals } from '@/lib/tag'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useAsync } from 'react-use'
import { usePrimaryPage, useSecondaryPage } from '@/PageManager'
import Nip29EventHeader from './Nip29EventHeader'

const BECH32_PREFIXES = ['npub1', 'nprofile1', 'nevent1', 'naddr1', 'note1']

export default function ForumTopic({
  event,
  className,
  clamp,
  showFull
}: {
  event: Event
  className?: string
  clamp?: boolean
  showFull?: boolean
}) {
  const title = useMemo(() => event.tags.find(tagNameEquals('title'))?.[1], [event])
  const { navigate } = usePrimaryPage()
  const { push } = useSecondaryPage()

  const {
    value: html,
    loading,
    error
  } = useAsync(() => renderDjotToHtml(event.content), [event.content])

  const onClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const anchor = target.closest('a')
    if (!anchor) return
    const href = anchor.getAttribute('href') || ''
    if (href.startsWith('nostrwiki:')) {
      e.preventDefault()
      const d = href.slice('nostrwiki:'.length)
      navigate('search', { input: `kind:30818 #d:${d}` })
      return
    }
    if (BECH32_PREFIXES.some((p) => href.startsWith(p))) {
      e.preventDefault()
      push(`/${href}`)
    }
  }

  const body = (
    <div
      className={cn(
        'prose prose-zinc max-w-none dark:prose-invert break-words overflow-wrap-anywhere',
        showFull && 'text-lg'
      )}
    >
      {loading && <p className="text-muted-foreground">Loading…</p>}
      {error && <pre className="whitespace-pre-wrap">{event.content}</pre>}
      {html && (
        <div dangerouslySetInnerHTML={{ __html: html }} onClick={onClick} className="cursor-auto" />
      )}
    </div>
  )

  return (
    <div className={cn('space-y-2', className)}>
      <Nip29EventHeader event={event} label="Forum Topic" />
      {title && (
        <div className={cn('text-xl font-semibold', !showFull && 'line-clamp-2')}>{title}</div>
      )}
      {clamp ? (
        <div className="relative overflow-hidden max-h-[300px]">
          {body}
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-b from-transparent to-background pointer-events-none">
            &nbsp;
          </div>
        </div>
      ) : (
        body
      )}
    </div>
  )
}
